import { apiClient } from '@/lib/api-client';
import type { SitePageContent, SitePageKey, UpsertSitePageContent } from '@/types/site-content';

/**
 * Storefront content pages.
 *
 * TWO SURFACES, deliberately kept apart, because they are two different actors:
 *
 *  - `siteContentService` is the SHOPKEEPER's, at MANAGER, scoped by the JWT's
 *    tenant. A returns policy is the shop's promise about the shop's money.
 *  - `siteContentModerationService` is the PLATFORM's, at MODERATOR, and can
 *    only set `admin_noindex` — the one lever against a tenant publishing spam
 *    on a hostname inside the registrable domain every other tenant shares.
 *
 * A tenant PUT cannot clear the platform hold: the API's UPDATE statement never
 * assigns that column, so it survives a re-publish by construction rather than
 * because a service remembered.
 */
export const siteContentService = {
  list() {
    return apiClient<SitePageContent[]>('/site/content');
  },

  /** The keys the platform allows, served rather than hardcoded because the
   *  list is frozen in a database CHECK that cannot be altered. */
  keys() {
    return apiClient<{ keys: SitePageKey[] }>('/site/content/keys');
  },

  get(pageKey: SitePageKey) {
    return apiClient<SitePageContent>(`/site/content/${pageKey}`);
  },

  /** PUT, not PATCH: a policy page is one document, and a partial update of an
   *  ordered block array has no sensible merge rule. */
  save(pageKey: SitePageKey, payload: UpsertSitePageContent) {
    return apiClient<SitePageContent>(`/site/content/${pageKey}`, {
      method: 'PUT',
      body: payload,
    });
  },

  remove(pageKey: SitePageKey) {
    return apiClient<{ removed: boolean }>(`/site/content/${pageKey}`, { method: 'DELETE' });
  },
};

export const siteContentModerationService = {
  list(tenantId: string) {
    return apiClient<SitePageContent[]>(`/tenants/${tenantId}/site/content`);
  },

  setHold(tenantId: string, pageKey: SitePageKey, adminNoindex: boolean, adminNote?: string) {
    return apiClient<SitePageContent>(`/tenants/${tenantId}/site/content/${pageKey}`, {
      method: 'PUT',
      body: { admin_noindex: adminNoindex, admin_note: adminNote },
    });
  },
};
